import React, { useCallback, useRef, useState } from 'react';

import { arrowSwitch, close, drag, gear, plus, warning } from '@/assets/icons';
import { SetlistSummaryCalendar } from '@/components/SetlistSummaryCard';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { Label } from '@/components/shared/Inputs/Label';
import { getRotationStatusClassName } from '@/components/shared/RotationStatusLabel';
import { SmallSongCardButton } from '@/components/shared/SmallSongCardButton';
import { Tabs } from '@/components/shared/Tabs';
import {
	SmallTeamSongCard,
	SmallTeamSongCardProps,
} from '@/components/SmallTeamSongCard';
import { Songlist360Report } from '@/components/Songlist360Report';
import { useCreateSetlistMutation, useUpdateSetlistMutation } from '@/graphql';
import { checkRotationStatusWarning } from '@/helpers/checkRotationStatusWarning';
import { classNames } from '@/helpers/classNames';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	restrictToParentElement,
	restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { InputAdornment, SvgIcon, useMediaQuery } from '@material-ui/core';
import { format, getDay } from 'date-fns';
import { last } from 'ramda';

import {
	getZonedStartDateTimeISO,
	IPropsCreateSetlistDisplay,
} from '../../CreateSetlistPanel';
import EnergyFlow from './components/EnergyFlow/EnergyFlow';
import GospelFlow from './components/GospelFlow/GospelFlow';
import { SecondStep } from './components/SecondStep/SecondStep';
import styles from './CreateSetlistRoot.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

export const CreateSetlistRoot: React.FC<IPropsCreateSetlistDisplay> = ({
	isViewOnly,
	config,
	handleClose,
	startTime,
	startDate,
	selectedSongIds,
	selectedServiceId,
	draftSetlist,
	setSelectedSongIds,
	openSong,
	openSonglistWithCallback,
	setStartDate,
	setStartTime,
	setSelectedServiceId,
	saveDraftSetlist,
	setAccountOpen,
}) => {
	const { refetch } = useSetlists();
	const { services } = useServices();
	const { handleError } = useErrorHandler();
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const songsListElement = useRef<HTMLDivElement | null>(null);

	const { mutateAsync: createMutateAsync } = useCreateSetlistMutation();
	const { mutateAsync: updateMutateAsync } = useUpdateSetlistMutation();

	const fireAddSongsEvent = useAnalyticsEvent('Create_Setlist_Add_Songs');
	const fireCreatedEvent = useAnalyticsEvent('Create_Setlitst_Created');

	const [tabIndex, setTabIndex] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [isWarningOpen, setIsWarningOpen] = useState<
		React.ReactNode | undefined
	>();
	const [isServicesPanelOpen, setIsServicesPanelOpen] = useState(false);

	const handleAddSongs = useCallback(() => {
		openSonglistWithCallback(
			(songIds, navigatedAway) => {
				if (songIds.length) {
					setSelectedSongIds([...selectedSongIds, ...songIds]);
					fireAddSongsEvent();
				}
				if (navigatedAway) {
					handleClose({});
				}
			},
			{
				actionLabelVerb: 'Select',
				title: 'Select setlist songs',
				excludeTeamSongIds: selectedSongIds,
				selectedServiceId,
			},
		);
	}, [
		openSonglistWithCallback,
		selectedSongIds,
		setSelectedSongIds,
		fireAddSongsEvent,
		handleClose,
		selectedServiceId,
	]);

	const noSongs = !selectedSongIds.length;
	const selectedService = services.find((s) => s.id === selectedServiceId);

	const saveSetlist = async () => {
		try {
			let setlistId = '';
			const input = {
				startAt: getZonedStartDateTimeISO(startTime, startDate),
				teamSongIds: selectedSongIds,
				serviceId: selectedServiceId,
				draft: false,
				generateSettings: {
					totalSongPositions: 4,
					postSermonSongPosition: 3,
				},
			};
			if (draftSetlist) {
				await updateMutateAsync({
					setlistId: draftSetlist.id,
					input,
				});
				setlistId = draftSetlist.id;
			} else {
				const res = await createMutateAsync({
					input,
				});
				setlistId = res.createSetlist.id;
			}
			if (!setlistId) {
				throw new Error('Setlist id was not present');
			}
			// If we are saving a real setlist, then we need to
			// send a chat message and handle some post actions related
			// to created a real setlist. Chech where the CreateSetlistPanel was
			// called from to see what the post actions are.
			handleClose({
				setlistId,
				songsLength: selectedSongIds.length,
			});
			fireCreatedEvent();

			refetch();
		} catch (error) {
			handleError({
				errorCode: 'SetlistCreateError',
				error,
				friendlyMessage: `There was an issue with saving your setlist`,
			});
		}
	};

	const handleAction = async () => {
		const isWeekDayValid = selectedService
			? !!startDate && selectedService.day === getDay(new Date(startDate))
			: true;
		if (startTime && startDate && isWeekDayValid) {
			saveSetlist();
		}
	};

	const handleDragEnd = (event) => {
		const { active, over } = event;
		setIsDragging(false);

		if (active.id !== over.id) {
			setSelectedSongIds((items) => {
				const oldIndex = items.indexOf(active.id);
				const newIndex = items.indexOf(over.id);

				return arrayMove(items, oldIndex, newIndex);
			});
		}
	};

	const onWarningClick = (warning: React.ReactNode) => {
		setIsWarningOpen(warning);
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={() => {
				setIsDragging(true);
				Haptics.impact({ style: ImpactStyle.Medium });
			}}
			onDragEnd={handleDragEnd}
			modifiers={[restrictToVerticalAxis, restrictToParentElement]}
		>
			<BottomActionLayout
				contentClassName={classNames([
					styles['root'],
					isDragging &&
						(isCapacitor || !isTablet) &&
						styles['disable-scroll'],
				])}
				actionDivider
				actionHidden={isViewOnly}
				bottomSheetOpen={!!isWarningOpen}
				bottomSheetContent={
					<RotationStatusWarningMemoized warning={isWarningOpen} />
				}
				onBottomSheetClose={() => setIsWarningOpen('')}
				bottomPanelOpen={isServicesPanelOpen}
				bottomPanelContent={
					<SecondStep
						startTime={startTime}
						startDate={startDate}
						setStartDate={setStartDate}
						setStartTime={setStartTime}
						setSelectedServiceId={setSelectedServiceId}
						selectedServiceId={selectedServiceId}
						onSave={() => setIsServicesPanelOpen(false)}
						setAccountOpen={setAccountOpen}
					/>
				}
				onBottomPanelClose={() => setIsServicesPanelOpen(false)}
				actionContent={
					<>
						<AsaphButton
							onClick={saveDraftSetlist}
							label="Save Draft"
							color="grey"
							disabled={!selectedSongIds.length}
						/>
						<AsaphButton
							color="blue"
							label={'Share setlist'}
							onClick={handleAction}
							disabled={!selectedSongIds.length}
						/>
					</>
				}
			>
				{!isViewOnly && (
					<>
						<Label className={styles['label']} withOffset>
							Service Settings
						</Label>
						<FormInput
							name="date"
							id="date"
							placeholder="Select a regular or custom service"
							disabled
							value={
								selectedServiceId === null
									? 'Custom service'
									: selectedService?.name
							}
							inputClassName={styles['service-settings']}
							onClick={() => setIsServicesPanelOpen(true)}
							startAdornment={
								startTime && startDate ? (
									<SetlistSummaryCalendar
										className={
											styles['service-field-calendar']
										}
										startAtISO={getZonedStartDateTimeISO(
											startTime,
											startDate,
										)}
										size="md"
									/>
								) : null
							}
							// onBlur={handleBlur}
							endAdornment={
								<InputAdornment
									position="end"
									classes={{
										root: styles['service-field-adornment'],
									}}
								>
									<SvgIcon component={gear} />
								</InputAdornment>
							}
						/>
					</>
				)}

				<Label withOffset className={styles['label']}>
					Setlist
				</Label>
				<div
					className={styles['add-songs-wrapper']}
					ref={songsListElement}
				>
					{noSongs ? (
						<div className={styles['button-row']}>
							<AsaphButton
								onClick={handleAddSongs}
								label="Add Songs"
								color="blue"
								fullWidth
								className={styles['add-button']}
								disabled={selectedServiceId === undefined}
							/>
							{/* <div className={styles['word']}>or</div>

							<SmallSongCardButton
								className={styles['add-button']}
								icon={sparkles}
								onClick={() => setSelectedSongIds([])}
							>
								Generate
							</SmallSongCardButton> */}
						</div>
					) : (
						<>
							<div
								className={classNames([
									styles['song-card-list'],
									!isViewOnly && styles['margin'],
								])}
							>
								{isViewOnly ? (
									<>
										{selectedSongIds.map((sid) => (
											<SmallTeamSongCard
												className={styles['song-card']}
												key={`small-song-card-${sid}`}
												teamSongId={sid}
												onClick={(song) => {
													openSong({
														teamSongId: song.id,
														viewOnly: false,
													});
												}}
											/>
										))}
									</>
								) : (
									<SortableContext
										items={selectedSongIds}
										strategy={verticalListSortingStrategy}
									>
										{selectedSongIds.map((sid) => (
											<SortableItem
												key={`small-song-card-${sid}`}
												id={sid}
												startDate={startDate}
												startTime={startTime}
												selectedServiceId={
													selectedServiceId
												}
												onClick={(song) => {
													openSong({
														teamSongId: song.id,
														viewOnly: false,
													});
												}}
												onSwitchClick={() => {
													openSonglistWithCallback(
														(
															songIds,
															navigatedAway,
														) => {
															if (
																songIds.length
															) {
																setSelectedSongIds(
																	selectedSongIds.map(
																		(
																			ssid,
																		) => {
																			return ssid ===
																				sid
																				? songIds[0]
																				: ssid;
																		},
																	),
																);
																fireAddSongsEvent();
															}
															if (navigatedAway) {
																handleClose({});
															}
														},
														{
															actionLabelVerb:
																'Switch',
															title: 'Switch song',
															excludeTeamSongIds:
																selectedSongIds,
															selectOnlyOne: true,
															noIndex: true,
														},
													);
												}}
												onLockClick={() => {}}
												onRemoveClick={() => {
													setSelectedSongIds(
														selectedSongIds.filter(
															(id) => id !== sid,
														),
													);
												}}
												onWarningClick={onWarningClick}
											/>
										))}
									</SortableContext>
								)}
							</div>
							{!isViewOnly && (
								<div className={styles['button-row']}>
									<SmallSongCardButton
										dashed
										icon={plus}
										onClick={handleAddSongs}
									>
										Add Songs
									</SmallSongCardButton>

									<div className={styles['word']}>or</div>

									<SmallSongCardButton
										icon={close}
										onClick={() => setSelectedSongIds([])}
									>
										Clear all
									</SmallSongCardButton>
								</div>
							)}
						</>
					)}
				</div>

				{noSongs ? null : (
					<>
						<Tabs
							className={styles['tabs']}
							maskClassName={styles['tabs-mask']}
							onChange={(selectedIndex) =>
								setTabIndex(selectedIndex)
							}
							items={[
								{
									title: 'Flow',
								},
								{
									title: 'Setlist 360',
								},
							]}
						/>
						<CurrentTabContent
							teamSongIds={selectedSongIds}
							tabIndex={tabIndex}
							onSongClick={(song) => {
								openSong({ teamSongId: song.id });
							}}
							isViewOnly={isViewOnly}
						/>
					</>
				)}
			</BottomActionLayout>
		</DndContext>
	);
};

const RotationStatusWarning: React.FC<{ warning: React.ReactNode }> = ({
	warning,
}) => {
	return (
		<div className={styles['warning-sheet']}>
			<div className={styles['title']}>Rotation Status Warning</div>
			<div className={styles['body']}>{warning}</div>
		</div>
	);
};

const RotationStatusWarningMemoized = React.memo(RotationStatusWarning);

const SortableItem: React.FC<{
	id: string;
	selectedServiceId?: string | null;
	startTime: IPropsCreateSetlistDisplay['startTime'];
	startDate: IPropsCreateSetlistDisplay['startDate'];
	onClick: SmallTeamSongCardProps['onClick'];
	onSwitchClick: () => void;
	onRemoveClick: () => void;
	onLockClick: () => void;
	onWarningClick: (warning: React.ReactNode) => void;
}> = ({
	id,
	selectedServiceId,
	startTime,
	startDate,
	onClick,
	onSwitchClick,
	onRemoveClick,
	onLockClick,
	onWarningClick,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });
	const { previousSetlists } = useSetlists();
	const { findTeamSongsById } = useTeamSongs();
	const teamSongs = findTeamSongsById([id]);
	const teamSong = teamSongs[0];
	const rotationStatusClass = getRotationStatusClassName(
		teamSong?.rotationStatus,
	);

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const teamSongLastPlayedAt = (teamSong?.setlists || [])
		.reduce<string[]>((acc, s) => {
			const setlist = previousSetlists.find(
				(ps) => ps.id === s.id && ps.serviceId === selectedServiceId,
			);
			if (setlist) {
				return [...acc, setlist.startAt];
			}
			return acc;
		}, [])
		.splice(-2); // Get the last two setlists where this song was used
	const lastPlayedAt = last(teamSongLastPlayedAt); // Get the last time the song was played
	const comparisonISODate = getZonedStartDateTimeISO(startTime, startDate);

	const rotationStatusWarning =
		!!teamSong &&
		checkRotationStatusWarning({
			lastPlayedAt: teamSongLastPlayedAt,
			rotationStatus: teamSong.rotationStatus,
			comparisonISODate,
		});

	return (
		<div
			className={classNames([
				styles['song-card-draggable'],
				isDragging && styles['dragging'],
			])}
			ref={setNodeRef}
			style={style}
			{...attributes}
		>
			{rotationStatusWarning ? (
				<SortableItemAction
					className={classNames([
						styles['warning'],
						rotationStatusClass,
					])}
					component={warning}
					onClick={() =>
						onWarningClick(
							`${rotationStatusWarning} ${
								lastPlayedAt
									? `It was last played on ${format(
											new Date(lastPlayedAt),
											'dd MMMM yyyy',
									  )}.`
									: ``
							}`,
						)
					}
				/>
			) : null}

			<div className={styles['song-card-wrapper']}>
				<SmallTeamSongCard
					className={styles['song-card']}
					teamSongId={id}
					onClick={onClick}
				/>
			</div>
			<SortableItemAction
				component={close}
				className={styles['first']}
				onClick={onRemoveClick}
			/>
			<SortableItemAction
				component={arrowSwitch}
				onClick={onSwitchClick}
			/>
			{/* <SortableItemAction component={lockOpened} onClick={onLockClick} /> */}
			<div className={styles['handle']}>
				<SvgIcon
					component={drag}
					ref={(e) => setActivatorNodeRef(e as HTMLElement | null)}
					{...listeners}
				/>
			</div>
		</div>
	);
};

const SortableItemAction: React.FC<
	React.HTMLAttributes<HTMLDivElement> & {
		component: React.ElementType;
	}
> = ({ component, className, ...genericHtmlDivProps }) => {
	return (
		<div
			className={classNames([styles['action'], className])}
			{...genericHtmlDivProps}
		>
			<SvgIcon component={component} />
			<div className={styles['background']} />
		</div>
	);
};

function shuffle(array) {
	let currentIndex = array.length;

	// While there remain elements to shuffle...
	while (currentIndex != 0) {
		// Pick a remaining element...
		let randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex--;

		// And swap it with the current element.
		[array[currentIndex], array[randomIndex]] = [
			array[randomIndex],
			array[currentIndex],
		];
	}
}

const CurrentTabContent: React.FC<{
	tabIndex: number;
	teamSongIds: string[];
	onSongClick?: SmallTeamSongCardProps['onClick'];
	isViewOnly: IPropsCreateSetlistDisplay['isViewOnly'];
}> = ({ tabIndex, teamSongIds, onSongClick, isViewOnly }) => {
	const fireViewMoreEvent = useAnalyticsEvent(
		isViewOnly ? 'View_Setlist_360_View_More' : 'Create_Setlist_View_More',
	);

	switch (tabIndex) {
		case 0:
			return (
				<>
					<EnergyFlow teamSongIds={teamSongIds} />
					<GospelFlow teamSongIds={teamSongIds} className="mt-sm" />
				</>
			);
		case 1:
			return (
				<Songlist360Report
					onSongClick={onSongClick}
					songIds={teamSongIds}
					onViewMoreClick={(tagCategory) => {
						fireViewMoreEvent({
							tagCategory,
						});
					}}
					className={styles['report']}
				/>
			);
		default:
			return null;
	}
};
